import { createFileRoute } from '@tanstack/react-router'
import { ArrowUpRight } from 'lucide-react'
import SquiggleAccent from '@/components/editorial/SquiggleAccent'
import CopyEmailButton from '@/components/preview/CopyEmailButton'
import Magnetic from '@/components/preview/Magnetic'
import ScrollProgress from '@/components/preview/ScrollProgress'
import WorkIndexList from '@/components/preview/WorkIndexList'
import { caseStudyDiscovery, workPageSeo } from '@/data/caseStudyDiscovery'
import { portfolioData } from '@/data/portfolio'

export const Route = createFileRoute('/work/')({
  head: () => ({
    meta: [
      { title: workPageSeo.title },
      { name: 'description', content: workPageSeo.description },
      { name: 'robots', content: 'index, follow, max-image-preview:large' },
      { property: 'og:title', content: workPageSeo.title },
      { property: 'og:description', content: workPageSeo.description },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: workPageSeo.url },
      {
        property: 'og:image',
        content: 'https://www.suriyaprakash.in/og/default.png',
      },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: workPageSeo.title },
      { name: 'twitter:description', content: workPageSeo.description },
      {
        name: 'twitter:image',
        content: 'https://www.suriyaprakash.in/og/default.png',
      },
    ],
    links: [{ rel: 'canonical', href: workPageSeo.url }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          '@id': `${workPageSeo.url}#page`,
          name: workPageSeo.title,
          description: workPageSeo.description,
          url: workPageSeo.url,
          author: {
            '@type': 'Person',
            name: portfolioData.personal.name,
            url: 'https://www.suriyaprakash.in/',
          },
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: caseStudyDiscovery.map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: item.name,
              url: `${workPageSeo.url}/${item.slug}`,
            })),
          },
        }).replace(/</g, '\\u003c'),
      },
    ],
  }),
  component: WorkIndex,
})

function WorkIndex() {
  const { personal } = portfolioData

  return (
    <div className="preview-page bg-cream-50 text-ink">
      <ScrollProgress />

      <header className="relative overflow-hidden bg-forest-strong pb-20 pt-28 text-cream-50 sm:pb-28 sm:pt-36">
        <div
          className="preview-blueprint absolute inset-0"
          aria-hidden="true"
        />
        <span
          className="preview-cross left-6 top-28 hidden sm:block"
          aria-hidden="true"
        />
        <span
          className="preview-cross right-6 top-28 hidden sm:block"
          aria-hidden="true"
        />

        <div className="container-custom relative">
          <div className="flex items-center justify-between">
            <a
              href="/"
              className="focus-ring preview-mono inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-sage-200 transition-colors hover:text-cream-50"
            >
              ← Portfolio
            </a>
            <p className="preview-mono hidden text-[10px] font-bold uppercase tracking-[0.24em] text-cream-50/65 sm:block">
              Case studies — {caseStudyDiscovery.length} projects
            </p>
          </div>

          <div className="mt-16 grid gap-12 sm:mt-24 lg:grid-cols-[minmax(0,1fr)_24rem] lg:items-end lg:gap-16">
            <div>
              <h1 className="preview-fade-up max-w-5xl font-heading display-xl">
                Case studies,
                <br />
                <SquiggleAccent className="italic text-sage-200">
                  built end to end.
                </SquiggleAccent>
              </h1>

              <p
                className="preview-fade-up mt-8 max-w-xl text-sm leading-7 text-cream-50/80 sm:mt-10 sm:text-base sm:leading-8"
                style={{ animationDelay: '150ms' }}
              >
                Production websites and permissioned web applications, from
                healthcare to publishing. Each case study explains the need,
                what I built and the engineering decisions behind it, with live
                sites or demos to explore.
              </p>
            </div>
            <nav
              aria-label="Case study index"
              className="preview-fade-up border-t border-white/15"
              style={{ animationDelay: '250ms' }}
            >
              {caseStudyDiscovery.map((item, index) => (
                <a
                  key={item.slug}
                  href={`/work/${item.slug}`}
                  className="focus-ring group flex items-center gap-5 border-b border-white/15 py-5 transition-colors hover:text-sage-200"
                >
                  <span className="preview-mono text-[10px] font-bold text-sage-200">
                    0{index + 1}
                  </span>
                  <span className="flex-1 font-heading text-2xl tracking-tight">
                    {item.name}
                  </span>
                  <ArrowUpRight className="size-4 shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
              ))}
            </nav>
          </div>
        </div>
      </header>

      <section
        aria-label="Project case studies"
        className="bg-cream-50 py-20 sm:py-28"
      >
        <div className="container-custom">
          <p className="preview-mono mb-8 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.24em] text-muted">
            <span>Case studies</span>
            <span className="text-forest">+</span>
          </p>
          <WorkIndexList items={caseStudyDiscovery} />
        </div>
      </section>

      <section className="bg-cream-50 pb-20 sm:pb-28">
        <div className="container-custom">
          <div className="grid gap-10 rounded-[2rem] bg-forest-strong p-7 text-cream-50 sm:p-12 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-end lg:gap-16">
            <div className="max-w-3xl">
              <p className="preview-mono text-[10px] font-bold uppercase tracking-[0.22em] text-sage-200">
                Next project
              </p>
              <h2 className="mt-4 font-heading text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.95] tracking-[-0.045em]">
                Working on something
                <br />
                <SquiggleAccent className="italic text-sage-200">
                  in mind?
                </SquiggleAccent>
              </h2>
              <p className="mt-5 max-w-2xl text-sm leading-7 text-cream-50/80 sm:text-base sm:leading-8">
                I’m open to full-time roles and selected freelance projects,
                from business websites to customer portals and internal
                applications. Tell me about the role or project and we can see
                whether I’m the right fit.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col [&>*]:w-full [&>*]:justify-center">
              <Magnetic>
                <a
                  href={`mailto:${personal.email}?subject=Website%20and%20platform%20development%20enquiry`}
                  className="focus-ring inline-flex w-full items-center justify-center gap-2 rounded-full bg-sage-200 px-7 py-4 text-sm font-bold text-forest-strong transition-colors hover:bg-cream-50"
                >
                  Discuss your project
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </Magnetic>
              <CopyEmailButton email={personal.email} />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
